import { clsx } from "clsx";
import type { InputHTMLAttributes, ChangeEventHandler } from "react";
import { forwardRef } from "react";

export interface CpsCheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  title: string;
  description?: string;
  value: string;
  color?: string;
  checked: boolean;
  onChange: ChangeEventHandler<HTMLInputElement>;
}

const colors: Record<string, string> = {
  primary: "peer-checked:bg-primary-400 peer-checked:border-primary-400",
  secondary: "peer-checked:bg-secondary-400 peer-checked:border-secondary-400",
};

export const CpsCheckbox = forwardRef<HTMLInputElement, CpsCheckboxProps>(
  (
    {
      title,
      description,
      checked,
      color = "secondary",
      onChange,
      id,
      value,
      ...props
    }: CpsCheckboxProps,
    ref,
  ) => {
    const labelClasses =
      "relative flex h-full w-full cursor-pointer items-center rounded-2xl border-2 border-neutral-200 bg-white py-4 px-4 text-sm font-medium peer-checked:text-white ";

    return (
      <div className="flex items-center">
        <input
          ref={ref}
          {...props}
          checked={checked}
          className="peer hidden"
          id={id}
          onChange={onChange}
          type="checkbox"
          value={value}
        />
        <label
          className={checked ? labelClasses.concat(colors[color]) : labelClasses}
          htmlFor={id}
        >
          <div
            className={clsx(
              "w-6 h-6 absolute flex items-center justify-center rounded-lg border-2 bg-white",
              checked ? "border-white" : "border-neutral-200",
            )}
          >
            {checked ? (
              <>
                <span
                  className={clsx(
                    "absolute left-[5px] top-[9px] h-[5px] w-[3px] -rotate-45 rounded-t-lg",
                    color === "secondary" ? "bg-secondary-400" : "bg-primary-400",
                  )}
                />
                <span
                  className={clsx(
                    "h-3 absolute right-[7px] top-1 w-[3px] rotate-45 rounded-lg",
                    color === "secondary" ? "bg-secondary-400" : "bg-primary-400",
                  )}
                />
              </>
            ) : (
              <div className="h-2 w-2 rounded-full bg-white" />
            )}
          </div>
          <div className="ml-8 flex flex-col">
            <p className={clsx(checked ? "text-white" : "text-neutral-500")}>{title}</p>
            {description ? (
              <p
                className={clsx(
                  "font-xs font-light",
                  checked ? "text-white" : "text-neutral-400",
                )}
              >
                {description}
              </p>
            ) : null}
          </div>
        </label>
      </div>
    );
  },
);

export default CpsCheckbox;
